import { ingredientKeyFor, type Substitution, type SubstitutionStatus } from './substitutions'

export type SubstitutionDisplayState =
  | { status: 'none' }
  | { status: 'pending'; substitution: Substitution }
  | { status: 'approved'; substitution: Substitution }

function inDateRange(sub: Substitution, isoDate: string): boolean {
  return isoDate >= sub.startDate && isoDate <= sub.endDate
}

function toIsoDate(date: Date): string {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

function pickBySubmittedDesc(a: Substitution, b: Substitution): number {
  return b.submittedAt.localeCompare(a.submittedAt)
}

export function findDisplayState(
  substitutions: Substitution[],
  ingredientName: string,
  ingredientUnit: string,
  referenceDate: Date,
): SubstitutionDisplayState {
  const key = ingredientKeyFor(ingredientName, ingredientUnit)
  const iso = toIsoDate(referenceDate)
  const candidates = substitutions.filter((s) => s.ingredientKey === key && inDateRange(s, iso))
  if (candidates.length === 0) {
    return { status: 'none' }
  }
  const approved = candidates.filter((s) => s.status === 'approved').sort(pickBySubmittedDesc)
  if (approved.length > 0) {
    return { status: 'approved', substitution: approved[0]! }
  }
  const pending = candidates.filter((s) => s.status === 'pending').sort(pickBySubmittedDesc)
  if (pending.length > 0) {
    return { status: 'pending', substitution: pending[0]! }
  }
  return { status: 'none' }
}

export function findAnyByIngredient(
  substitutions: Substitution[],
  ingredientName: string,
  ingredientUnit: string,
): Substitution[] {
  const key = ingredientKeyFor(ingredientName, ingredientUnit)
  return substitutions.filter((s) => s.ingredientKey === key)
}

export function findActiveByIngredient(
  substitutions: Substitution[],
  ingredientName: string,
  ingredientUnit: string,
): Substitution | null {
  const key = ingredientKeyFor(ingredientName, ingredientUnit)
  const active = substitutions.filter((s) => s.ingredientKey === key && s.status !== 'rejected')
  if (active.length === 0) {
    return null
  }
  const approved = active.filter((s) => s.status === 'approved').sort(pickBySubmittedDesc)
  if (approved.length > 0) {
    return approved[0]!
  }
  return active.sort(pickBySubmittedDesc)[0]!
}

export function countByStatus(substitutions: Substitution[]): Record<SubstitutionStatus, number> {
  const out: Record<SubstitutionStatus, number> = { pending: 0, approved: 0, rejected: 0 }
  for (const s of substitutions) {
    out[s.status] += 1
  }
  return out
}

export function filterInRange(substitutions: Substitution[], rangeStart: Date, rangeEnd: Date): Substitution[] {
  const start = toIsoDate(rangeStart)
  const end = toIsoDate(rangeEnd)
  return substitutions.filter((s) => !(s.endDate < start || s.startDate > end))
}
